import axios from "axios";

// Public (no-login) NHSO dashboard store: fetches GET /nhsoPublicDashboard and
// caches each filter combination in-memory with a short client TTL.
const TTL_MS = 5 * 60 * 1000; // 5 min client cache (server caches ~15 min)

const fresh = entry => entry && Date.now() - entry.fetchedAt < TTL_MS;
const cacheKey = ({ period, areaType, area, subitem }) =>
  `${period}|${areaType}|${area}|${subitem}`;

// DEV-ONLY: before the backend endpoint is deployed, fall back to a generated
// fixture so the page can be built/verified. Never used in production builds.
const loadFixture = async params => {
  const { buildFixture } = await import("@/utils/nhsoDashboardFixture");
  return buildFixture(params);
};

const moduleNhsoDashboard = {
  namespaced: true,
  state: { byKey: {} }, // key -> { data, fetchedAt }
  mutations: {
    set(state, { key, data }) {
      state.byKey = { ...state.byKey, [key]: { data, fetchedAt: Date.now() } };
    },
  },
  getters: {
    dashboard: state => params => {
      const entry = state.byKey[cacheKey(params)];
      return entry ? entry.data : null;
    },
  },
  actions: {
    async fetchDashboard(
      { state, commit },
      { period, areaType, area, subitem, force = false, mock = false }
    ) {
      const key = cacheKey({ period, areaType, area, subitem });
      if (!force && fresh(state.byKey[key])) return state.byKey[key].data;

      // Explicit dev mock: skip the network entirely.
      if (mock) {
        const data = await loadFixture({ period, areaType, area, subitem });
        commit("set", { key, data });
        return data;
      }

      try {
        const res = await axios.get("/api/nhsoPublicDashboard", {
          params: { period, areaType, area, subitem },
          skipGlobalErrorHandler: true,
        });
        commit("set", { key, data: res.data });
        return res.data;
      } catch (err) {
        // Until the endpoint ships, keep the page usable in non-prod by serving
        // the fixture. In production this rethrows so the UI shows its error.
        if (process.env.NODE_ENV !== "production") {
          const data = await loadFixture({ period, areaType, area, subitem });
          commit("set", { key, data });
          return data;
        }
        throw err;
      }
    },
  },
};

export default moduleNhsoDashboard;
